import { supabaseClient } from "@/lib/supabase";
import type { StatefulTrackerViewT } from "@patient-watch/types";

export interface WorklistPatientInfo {
    patient_name: string | null;
    patient_email: string | null;
    date_of_birth: string | null;
}

export interface ClinicianWorklistDiary {
    diary_id: string;
    patient_id: string | null;
    hospital_number: string | null;
    organisation_name: string | null;
    patient_info: WorklistPatientInfo | null;
    sequential_forms: boolean;
    trackers_view: StatefulTrackerViewT[];
}

export interface ClinicianWorklistResult {
    diaries: ClinicianWorklistDiary[];
    total: number;
    truncated: boolean;
}

/** Clinical procedure kind used for incomplete-details task labels and deep links. */
export type WorklistProcedureKind = "injection" | "surgery" | "shockwave" | "procedure";

export interface IncompleteDetailsTarget {
    eventId: string;
    trackerId: string;
    procedureKind: Exclude<WorklistProcedureKind, "procedure">;
}

export interface IncompleteDataDiary {
    diary_id: string;
    patient_id: string | null;
    hospital_number: string | null;
    organisation_name: string | null;
    patient_info: WorklistPatientInfo | null;
    has_incomplete_events: boolean;
    procedureKind: WorklistProcedureKind;
    /** Deep-link target for the intervention-details form, when resolvable. */
    detailsTarget: IncompleteDetailsTarget | null;
}

interface EventKindRow {
    event_id: string;
    diary_id: string | null;
    injections: { injection_id: string }[] | null;
    surgeries: { surgery_id: string }[] | null;
    shockwaves: { shockwave_id: string }[] | null;
}

/** Prefer a single specific kind; fall back to procedure when mixed or unknown. */
export function resolveWorklistProcedureKind(events: EventKindRow[]): WorklistProcedureKind {
    let hasInjection = false;
    let hasSurgery = false;
    let hasShockwave = false;

    for (const event of events) {
        if ((event.injections?.length ?? 0) > 0) hasInjection = true;
        if ((event.surgeries?.length ?? 0) > 0) hasSurgery = true;
        if ((event.shockwaves?.length ?? 0) > 0) hasShockwave = true;
    }

    const kinds = [hasSurgery && "surgery", hasInjection && "injection", hasShockwave && "shockwave"].filter(
        Boolean
    ) as WorklistProcedureKind[];

    if (kinds.length === 1) return kinds[0];
    return "procedure";
}

const WORKLIST_PROCEDURE_KINDS = new Set<WorklistProcedureKind>(["injection", "surgery", "shockwave", "procedure"]);

interface IncompleteEventRpcRow {
    diary_id: string;
    patient_id: string | null;
    hospital_number: string | null;
    organisation_name: string | null;
    patient_info: WorklistPatientInfo | Record<string, unknown> | null;
    event_id: string;
    procedure_kind: string;
    tracker_id: string | null;
}

/** The incomplete-events RPC is newer than the generated database types. */
interface WorklistRpcClient {
    rpc: (
        fn: string,
        args?: Record<string, unknown>
    ) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
}

function normalisePatientInfo(info: IncompleteEventRpcRow["patient_info"]): WorklistPatientInfo | null {
    if (!info || typeof info !== "object") return null;
    const record = info as Record<string, unknown>;
    return {
        patient_name: typeof record.patient_name === "string" ? record.patient_name : null,
        patient_email: typeof record.patient_email === "string" ? record.patient_email : null,
        date_of_birth: typeof record.date_of_birth === "string" ? record.date_of_birth : null,
    };
}

function parseProcedureKind(value: string): WorklistProcedureKind {
    return WORKLIST_PROCEDURE_KINDS.has(value as WorklistProcedureKind)
        ? (value as WorklistProcedureKind)
        : "procedure";
}

export async function getClinicianWorklist(userIds: string[] = []): Promise<ClinicianWorklistResult> {
    const { data, error } = await supabaseClient.functions.invoke("public-get-clinician-worklist", {
        body: { user_ids: userIds },
    });

    if (error) {
        console.error("🚨 [getClinicianWorklist] error:", error);
        throw error;
    }

    if (!data || typeof data !== "object") {
        return { diaries: [], total: 0, truncated: false };
    }

    const payload = data as ClinicianWorklistResult & { error?: string };
    if (payload.error) {
        throw new Error(payload.error);
    }

    return {
        diaries: Array.isArray(payload.diaries) ? payload.diaries : [],
        total: typeof payload.total === "number" ? payload.total : 0,
        truncated: Boolean(payload.truncated),
    };
}

/**
 * Incomplete procedure-detail tasks for the clinician worklist.
 * Uses get_clinician_worklist_incomplete_events instead of diaries_view:
 * a 40-id IN list still timed out because the view materialises diary_access.
 */
export async function getIncompleteDataDiaries(userIds: string[] = []): Promise<IncompleteDataDiary[]> {
    const { data, error } = await (supabaseClient as unknown as WorklistRpcClient).rpc(
        "get_clinician_worklist_incomplete_events",
        {
            p_user_ids: userIds,
            p_limit: 200,
        }
    );

    if (error) {
        console.error("🚨 [getIncompleteDataDiaries] error:", error);
        throw error;
    }

    return ((data ?? []) as IncompleteEventRpcRow[]).flatMap((row) => {
        if (!row.diary_id || !row.event_id) return [];
        const procedureKind = parseProcedureKind(row.procedure_kind);
        const detailsTarget: IncompleteDetailsTarget | null =
            procedureKind === "procedure"
                ? null
                : {
                      eventId: row.event_id,
                      trackerId: row.tracker_id ?? "none",
                      procedureKind,
                  };
        return [
            {
                diary_id: row.diary_id,
                patient_id: row.patient_id,
                hospital_number: row.hospital_number,
                organisation_name: row.organisation_name,
                has_incomplete_events: true,
                procedureKind,
                detailsTarget,
                patient_info: normalisePatientInfo(row.patient_info),
            },
        ];
    });
}

export interface WorklistConsent {
    diary_id: string;
    patient_id: string | null;
    hospital_number: string | null;
    organisation_name: string | null;
    patient_info: WorklistPatientInfo | null;
    consent_id: string;
    consent_name: string | null;
}

interface ConsentRpcRow {
    diary_id: string;
    patient_id: string | null;
    hospital_number: string | null;
    organisation_name: string | null;
    patient_info: WorklistPatientInfo | Record<string, unknown> | null;
    consent_id: string;
    consent_name: string | null;
}

/** Unsigned staff consents assigned to the clinician (or filtered staff). */
export async function getWorklistConsents(userIds: string[] = []): Promise<WorklistConsent[]> {
    const { data, error } = await (supabaseClient as unknown as WorklistRpcClient).rpc(
        "get_clinician_worklist_consents",
        {
            p_user_ids: userIds,
            p_limit: 200,
        }
    );

    if (error) {
        const message = error.message.toLowerCase();
        const missingRpc = message.includes("get_clinician_worklist_consents") || message.includes("pgrst202");
        if (missingRpc) {
            console.warn("⚠️ [getWorklistConsents] RPC not available yet:", error.message);
            return [];
        }
        console.error("🚨 [getWorklistConsents] error:", error);
        throw error;
    }

    return ((data ?? []) as ConsentRpcRow[]).flatMap((row) => {
        if (!row.diary_id || !row.consent_id) return [];
        return [
            {
                diary_id: row.diary_id,
                patient_id: row.patient_id,
                hospital_number: row.hospital_number,
                organisation_name: row.organisation_name,
                patient_info: normalisePatientInfo(row.patient_info),
                consent_id: row.consent_id,
                consent_name: typeof row.consent_name === "string" ? row.consent_name : null,
            },
        ];
    });
}
